import { Locale, getLocale } from '@toasttab/buffet-pui-locale-utilities'
import enUS from './locales/en-US.json'
import esUS from './locales/es-US.json'

export const defaultStrings = enUS

export type DefaultStrings = Record<
  keyof typeof defaultStrings,
  {
    message: string
    description?: string
  }
>

type DefaultFlattenedStrings = Record<keyof typeof defaultStrings, string>

type GetCountryStrings = (
  locale?: Locale
) => Record<keyof typeof defaultStrings, string>

// This flattens the source file so that the country strings in getCountryStrings can be more easily used elsewhere
const flattenObjectStrings = (
  strings: DefaultStrings
): DefaultFlattenedStrings => {
  const flattenedStrings = {} as Record<keyof typeof defaultStrings, string>
  for (const key of Object.keys(strings)) {
    if (typeof strings[key as keyof typeof strings] === 'object') {
      flattenedStrings[key as keyof typeof strings] =
        strings[key as keyof typeof strings].message
    }
  }
  return flattenedStrings
}

const flattenedDefaultStrings = flattenObjectStrings(defaultStrings)

export const getCountryStrings: GetCountryStrings = (locale) => {
  const language = locale || getLocale()
  switch (language) {
    case 'es-US':
      return addMissingCountryStrings(esUS)
    default:
      return flattenedDefaultStrings
  }
}

/**
 * Adds any missing translations from the defaultStrings object to the target locale file. Note the locale file is already flattened.
 * This is to mirror the en-US fallback behaviour of i18next.
 *
 * @param {Partial<DefaultFlattenedStrings>} localeStrings - The locale strings to which missing keys will be added.
 * @returns {DefaultFlattenedStrings} - The localeStrings object with any missing strings added in simple json format.
 */
function addMissingCountryStrings(
  localeStrings: Partial<DefaultFlattenedStrings>
): DefaultFlattenedStrings {
  const strings = { ...localeStrings } as DefaultFlattenedStrings
  for (const key in defaultStrings) {
    if (!strings.hasOwnProperty(key)) {
      const missingKey = key as keyof DefaultFlattenedStrings
      strings[missingKey] = flattenedDefaultStrings[missingKey]
    }
  }
  return strings
}
